import ky, { type Options } from 'ky';
import { isOidcConfigured, userManager } from './oidc';

const DEFAULT_TIMEOUT_MS = 30_000;
let silentRenewPromise: ReturnType<typeof userManager.signinSilent> | null = null;

function isReasonRequest(url: string): boolean {
  return url.startsWith('/api/seccapreason') && !url.endsWith('/client-events');
}

function emitReasonClientEvent(
  url: string,
  event: string,
  detail: { http_status?: number; error_code?: string } = {}
): void {
  if (!isReasonRequest(url) || typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('seccapreason:client-event', {
      detail: { event, ...detail },
    })
  );
}

async function renewAccessTokenOnce(): Promise<void> {
  if (!silentRenewPromise) {
    silentRenewPromise = userManager.signinSilent().finally(() => {
      silentRenewPromise = null;
    });
  }
  await silentRenewPromise;
}

const defaultOptions: Options = {
  throwHttpErrors: false,
  retry: 0,
  credentials: 'same-origin',
  timeout: DEFAULT_TIMEOUT_MS,
  hooks: {
    beforeRequest: [
      async ({ request }) => {
        if (!isOidcConfigured) return;
        const user = await userManager.getUser();
        if (user?.access_token) {
          request.headers.set('Authorization', `Bearer ${user.access_token}`);
        }
      },
    ],
  },
};

export class HttpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type ErrorWithCause = Error & { cause?: unknown };

function createErrorWithCause(message: string, cause: unknown): Error {
  const error = new Error(message) as ErrorWithCause;
  error.cause = cause;
  return error;
}

async function ensureOk(response: Response): Promise<void> {
  if (response.ok) return;
  const text = await response.text();
  throw new HttpError(text || `HTTP ${response.status}`, response.status);
}

function isLikelyHtmlDocument(text: string): boolean {
  const normalized = text.trimStart().toLowerCase();
  return normalized.startsWith('<!doctype html') || normalized.startsWith('<html');
}

async function parseBody<T>(response: Response, url: string): Promise<T | null> {
  const contentType = response.headers.get('content-type') || '';
  const contentLength = response.headers.get('content-length');
  if (response.status === 204 || contentLength === '0') {
    return null;
  }
  if (contentType.includes('text/html')) {
    throw new Error(
      `Expected JSON from ${url}, but received HTML. Check API routing/authentication.`
    );
  }
  if (contentType.includes('application/json')) {
    try {
      return (await response.json()) as T;
    } catch {
      return null;
    }
  }
  const text = await response.text();
  if (!text) return null;
  if (isLikelyHtmlDocument(text)) {
    throw new Error(
      `Expected JSON from ${url}, but received HTML. Check API routing/authentication.`
    );
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    return { raw: text } as T;
  }
}

export async function fetchJSON<T = unknown>(url: string, options?: Options): Promise<T | null> {
  const requestTimeoutMs =
    typeof options?.timeout === 'number' ? options.timeout : DEFAULT_TIMEOUT_MS;
  try {
    const response = await ky(url, { ...defaultOptions, ...options });

    if (response.status === 401 && isOidcConfigured) {
      emitReasonClientEvent(url, 'api_401', { http_status: 401 });
      const existingUser = await userManager.getUser();
      if (!existingUser) {
        // Token not in storage yet (race with login callback) — wait for it
        await Promise.race([
          new Promise<void>((resolve) => {
            const h = () => {
              userManager.events.removeUserLoaded(h);
              resolve();
            };
            userManager.events.addUserLoaded(h);
          }),
          new Promise<void>((resolve) => setTimeout(resolve, 5000)),
        ]);
      } else {
        // Token exists but expired — renew via refresh token (fast)
        try {
          emitReasonClientEvent(url, 'auth_refresh_started');
          await renewAccessTokenOnce();
          emitReasonClientEvent(url, 'auth_refresh_succeeded');
        } catch (error) {
          emitReasonClientEvent(url, 'auth_refresh_failed', {
            error_code: 'silent_refresh_failed',
          });
          window.dispatchEvent(new CustomEvent('mlp:auth-refresh-failed'));
          throw createErrorWithCause(
            'Authentication refresh failed. Retry the request or sign in again.',
            error
          );
        }
      }
      const retried = await ky(url, { ...defaultOptions, ...options });
      if (retried.status === 403) {
        emitReasonClientEvent(url, 'api_403', { http_status: 403 });
      } else if (retried.status >= 500) {
        emitReasonClientEvent(url, 'api_5xx', { http_status: retried.status });
      }
      await ensureOk(retried);
      return parseBody<T>(retried, url);
    }

    if (response.status === 403) {
      emitReasonClientEvent(url, 'api_403', { http_status: 403 });
    } else if (response.status >= 500) {
      emitReasonClientEvent(url, 'api_5xx', { http_status: response.status });
    }
    await ensureOk(response);
    return parseBody<T>(response, url);
  } catch (error) {
    if (error instanceof Error && error.name === 'TimeoutError') {
      emitReasonClientEvent(url, 'network_error', { error_code: 'timeout' });
      throw createErrorWithCause(
        `Request timed out after ${requestTimeoutMs / 1000}s: ${url}`,
        error
      );
    }
    if (!(error instanceof HttpError)) {
      emitReasonClientEvent(url, 'network_error', { error_code: 'request_failed' });
    }
    throw error;
  }
}
